import { OPPORTUNITIES_STATUS } from '../../../opportunities/components/helpers/opportunitiesListHelper';
import { PACK_TYPE } from '../../../shop/propTypes/productProp';

const checkIfIsNormalProduct = product =>
  !product.parentProduct.availableForSubscription ||
  (product.parentProduct.availableForSubscription &&
    product.parentProduct.availableSubscriptionFrequency.includes('only_once'));

const handleValidationFrequencies = (products, previousProducts = []) => {
  // Determine the active subscription frequency from previous products
  const previousSubscription = previousProducts.find(
    p => p.frequency !== 'only_once',
  );
  const activeFrequency = previousSubscription?.frequency;

  const supportsFrequency = (product, frequency) =>
    product?.parentProduct?.availableSubscriptionFrequency?.includes(frequency);

  if (activeFrequency) {
    // Filter: keep only products that support the active subscription frequency
    return products.filter(p => supportsFrequency(p, activeFrequency));
  }

  // No active subscription yet — determine frequency from new products
  const firstSubscription = products.find(p => p.frequency !== 'only_once');

  if (firstSubscription) {
    // Keep all products that support this subscription frequency
    return products.filter(p =>
      supportsFrequency(p, firstSubscription.frequency),
    );
  }

  // All products are only_once — return as-is
  return products;
};

const MODE = {
  CREATE: 'create',
  EDIT: 'edit',
};

const customerShippingAddress = user => {
  if (user && user.shippingCountry) {
    return (
      user.shippingAddress.find(s => s.countryCode === user.shippingCountry) ||
      null
    );
  }
  return user?.shippingAddress[0] || null;
};

/**
 * Manages the initial state
 * @param mode {string} Can be "edit" or "create"
 * @param isOpportunity {boolean} Indicates if the state is for an opportunity (true) or an order (false)
 * @param owner {UserProp} Represents the owner of the opportunity or order
 * @param customer {UserProp} represents the seller
 * @param productsSelector {MarketProductVariation[]} Array of product variations list and determine the currency
 * @param opportunity {OrderProp} Existing opportunity or empty object for creation
 * @returns {{
 *   owner: UserProp,               // The owner of the opportunity or order
 *   seller: null,                  // Seller information, initially null
 *   sellerShippingAddress: null,   // Seller's shipping address, initially null
 *   shipping: null,                // Shipping details, initially null
 *   discount: null,                // Discount information, initially null
 *   shippingAddress: null,         // Shipping address, initially null
 *   currency: string,              // Currency, defaults to 'eur' or the first product's userPrice.code
 *   billingAddress: null,          // Billing address, initially null
 *   products: [],                  // Product list, initially empty
 *   status: string|undefined       // Status, 'PENDING' for opportunity or undefined for order
 * }}
 */
const getInitialState = (
  mode,
  isOpportunity,
  owner,
  customer,
  productsSelector,
  opportunity,
  orderSubscription,
) => {
  const defaultState = {
    shipping: null,
    owner,
    discount: null,
    currency: productsSelector?.[0]?.userPrice?.code || 'eur',
    billingAddress: customer?.address || null,
    shippingAddress: customerShippingAddress(customer),
    products: [],
    seller: customer,
    sellerShippingAddress: null,
    status: isOpportunity ? OPPORTUNITIES_STATUS.PENDING : undefined,
  };

  if (orderSubscription) {
    return {
      ...defaultState,
      ...orderSubscription,
      shipping: orderSubscription?.shipping,
      discount: orderSubscription?.discount?.code || null,
      seller: orderSubscription.owner,
    };
  }

  switch (mode) {
    case MODE.EDIT:
      return isOpportunity && opportunity
        ? {
            ...defaultState,
            ...opportunity,
            seller: opportunity.owner,
            status: OPPORTUNITIES_STATUS.PENDING,
          }
        : defaultState;
    case MODE.CREATE:
    default:
      return defaultState;
  }
};

const getInitialShipping = (mode, opportunity, orderSubscription) => {
  if (orderSubscription) {
    return (
      orderSubscription?.billingAddress?.address_1 !==
      orderSubscription?.shippingAddress?.address_1
    );
  }

  switch (mode) {
    case MODE.EDIT:
      return (
        !opportunity?.operator &&
        opportunity?.billingAddress?.address_1 &&
        opportunity?.shippingAddress?.address_1
      );
    case MODE.CREATE:
    default:
      return false;
  }
};

/**
 * Manages the initial product state based on the mode and opportunity
 * @param mode {string} Can be "edit" or "create"
 * @param isOpportunity {boolean} Indicates if the state is for an opportunity (true) or an order (false)
 * @param opportunity {OrderProp} Existing opportunity containing products (used in "edit" mode)
 * @returns {Array} Array of products for the opportunity or an empty array
 */
const getInitialProductState = (mode, isOpportunity, opportunity) => {
  switch (mode) {
    case MODE.EDIT:
      return isOpportunity && opportunity
        ? opportunity?.products?.map(p => {
            if (p?.type === 'product') {
              const productVariation = p?.product;
              const userPriceToUse =
                productVariation?.userPromotionPrice?.price >= 0
                  ? productVariation?.userPromotionPrice
                  : productVariation?.userPrice;
              const isDigitalContent = p?.parentProduct
                ? p?.parentProduct?.isDigital
                : p?.digitalDocument && p?.digitalDocument?.url?.length > 0;
              return {
                ...p,
                ...productVariation,
                frequency: 'only_once',
                product: productVariation?._id,
                quantity: p?.quantity,
                quantityToOrder: p?.quantity,
                isDigitalContent,
                type: p?.type,
                userPriceToUse: {
                  ...userPriceToUse,
                  tax: userPriceToUse?.tax || p?.tax || 0,
                },
              };
            }
            const pack = p?.packProduct?.pack;
            const userPriceToUse =
              pack?.userPromotionPrice?.price >= 0
                ? pack?.userPromotionPrice
                : pack?.userPrice;
            const isDigitalContent = p?.parentProduct
              ? p?.parentProduct?.isDigital
              : p?.digitalDocument && p?.digitalDocument?.url?.length > 0;

            return {
              ...p,
              ...pack,
              ...p?.packProduct,
              frequency: 'only_once',
              product: p?._id,
              packProduct: p?.packProduct?._id,
              quantity: p?.quantity,
              quantityToOrder: p?.quantity,
              isDigitalContent,
              type: p?.type,
              userPriceToUse: {
                ...userPriceToUse,
                tax: userPriceToUse?.tax || p?.tax || 0,
              },
            };
          })
        : [];
    case MODE.CREATE:
    default:
      return [];
  }
};

const getInitialOperator = (mode, isOpportunity, opportunity) => {
  const defaultState = { operator: null, installationAddress: null };
  switch (mode) {
    case MODE.EDIT:
      return isOpportunity
        ? {
            operator: (isOpportunity && opportunity?.operator) || null,
            installationAddress:
              (isOpportunity && opportunity?.installationAddress) || null,
          }
        : defaultState;
    case MODE.CREATE:
    default:
      return defaultState;
  }
};

const getProductsForOrderCreate = (cartProducts, isOpportunity = false) =>
  cartProducts?.map(p => {
    const { isPack, packType, packVariations } = p;
    const isAdvancedPack = packType === 'advanced' && isPack;
    const isHybridPack = packType === PACK_TYPE.HYBRID_PACK;
    const productId = p._id;
    const quantity = p.quantityToOrder || 1;
    let result = {};

    // Normal Pack
    if (isPack && !isAdvancedPack && !isHybridPack) {
      result = {
        packProduct: productId,
        quantity,
        type: 'pack',
      };
    }

    // Advanced Pack
    if (isPack && isAdvancedPack) {
      result = {
        packProduct: productId,
        type: 'pack_advanced',
        quantity,
        packVariations: packVariations?.map(variation => ({
          variation: variation._id || variation?.variation,
          quantity: variation.quantity,
        })),
      };
    }

    if (isPack && isHybridPack) {
      return {
        packProduct: p?.parentProduct?._id,
        quantity,
        type: PACK_TYPE.HYBRID_PACK,
        packVariations: [
          ...(packVariations || []).map(variation => ({
            variation: variation._id,
            quantity: variation.quantity,
          })),
          ...(p?.parentProduct?.packVariations || []).map(variation => ({
            variation: variation.variation?._id,
            quantity: variation.quantity,
          })),
        ].reduce((acc, curr) => {
          const existing = acc.find(v => v.variation === curr.variation);
          if (existing) {
            existing.quantity += curr.quantity;
          } else {
            acc.push(curr);
          }
          return acc;
        }, []),
      };
    }

    if (!isPack) {
      // Normal product
      result = {
        product: productId,
        quantity,
        type: 'product',
      };
    }

    if (isOpportunity && p.form) {
      result.formAnswerData = p.formAnswerData;
      result.formAnswersLink = p.formAnswersLink;
    }

    return result;
  });

export {
  checkIfIsNormalProduct,
  handleValidationFrequencies,
  getInitialState,
  getInitialProductState,
  MODE,
  getInitialOperator,
  getInitialShipping,
  getProductsForOrderCreate,
};
